/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Shared touch-listener plumbing for hand-rolled single-touch gestures
 * (pull-to-refresh, transport swipe). Owns only the common wiring — which
 * listeners are passive and the symmetric teardown — so each gesture keeps
 * its own tracking/threshold logic in its own handlers.
 *
 * Framework-agnostic (no vinyl imports) so callers stay unit-testable with
 * plain jsdom / linkedom.
 */

export interface TouchGestureHandlers {
    readonly onTouchStart: (e: TouchEvent) => void
    readonly onTouchMove: (e: TouchEvent) => void
    readonly onTouchEnd: (e: TouchEvent) => void
    readonly onTouchCancel: (e: TouchEvent) => void
}

/**
 * Attach the four single-touch listeners to `el` and return a disposer that
 * removes them. `touchmove` is non-passive so a committed gesture can
 * `preventDefault()` native scroll/overscroll; start/end/cancel stay passive
 * so they never block scroll composition on the common non-gesture touch.
 */
export function bindTouchGesture(el: HTMLElement, handlers: TouchGestureHandlers): () => void {
    const { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel } = handlers
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
        el.removeEventListener('touchstart', onTouchStart)
        el.removeEventListener('touchmove', onTouchMove)
        el.removeEventListener('touchend', onTouchEnd)
        el.removeEventListener('touchcancel', onTouchCancel)
    }
}
